/**
 * Hero section for category landing pages
 */

import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Sparkles, Music, Zap, Calendar } from 'lucide-react';
import { getCategoryInfo } from '@/utils/filterUtils';

interface CategoryHeroProps {
  type: 'software' | 'holiday' | 'genre' | 'artist';
  value: string;
  totalResults: number;
  className?: string;
}

export const CategoryHero: React.FC<CategoryHeroProps> = ({
  type,
  value,
  totalResults,
  className
}) => {
  const categoryInfo = getCategoryInfo(type as 'software' | 'holiday', value);
  
  // Get appropriate icon based on category type
  const getIcon = () => {
    switch (type) {
      case 'software':
        return <Zap className="w-8 h-8 text-primary" />;
      case 'holiday':
        return <Calendar className="w-8 h-8 text-primary" />;
      case 'genre':
        return <Music className="w-8 h-8 text-primary" />;
      case 'artist':
        return <Sparkles className="w-8 h-8 text-primary" />;
      default:
        return <Sparkles className="w-8 h-8 text-primary" />;
    }
  };

  // Get background gradient based on category
  const getGradient = () => {
    switch (type) {
      case 'software':
        return 'from-blue-500/10 to-purple-500/10';
      case 'holiday':
        if (value.toLowerCase().includes('christmas')) {
          return 'from-red-500/10 to-green-500/10';
        } else if (value.toLowerCase().includes('halloween')) {
          return 'from-orange-500/10 to-purple-500/10';
        }
        return 'from-blue-500/10 to-purple-500/10';
      case 'genre':
        return 'from-pink-500/10 to-purple-500/10';
      case 'artist':
        return 'from-yellow-500/10 to-orange-500/10';
      default:
        return 'from-blue-500/10 to-purple-500/10';
    }
  };

  return (
    <Card className={`border-0 bg-linear-to-r ${getGradient()} ${className}`}>
      <CardContent className="p-8 md:p-12">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3 mb-4">
            {getIcon()}
            <Badge variant="secondary" className="text-sm px-3 py-1">
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </Badge>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">
            {categoryInfo.title}
          </h1>
          
          <p className="text-lg md:text-xl text-muted-foreground mb-6 leading-relaxed">
            {categoryInfo.description}
          </p>
          
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-2">
              <div className="w-2 h-2 bg-primary rounded-full"></div>
              {totalResults.toLocaleString()} sequences available
            </span>
            <span className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              Free and premium options
            </span>
            <span className="flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              Instant download
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};